import { memo } from 'react'

const H = ({ children }: { children: string }) => (
  <h3 className="mt-4 text-sm font-semibold text-white first:mt-0">{children}</h3>
)
const P = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => (
  <p className={`text-[12px] leading-relaxed text-[#cbd5e1] ${className}`.trim()}>{children}</p>
)
const UL = ({ children }: { children: React.ReactNode }) => (
  <ul className="ml-4 list-disc space-y-1 text-[12px] leading-relaxed text-[#cbd5e1]">{children}</ul>
)
const Meta = ({ children }: { children: string }) => (
  <div className="text-[11px] text-[#64748b]">{children}</div>
)

export const TosContent = memo(function TosContent() {
  return (
    <>
      <Meta>Version 1.0 · Effective date: 2026-04-23</Meta>
      <P>
        By using PolyStrike Visual (the &ldquo;Service&rdquo;, the &ldquo;App&rdquo;) you agree to these Terms.
        If you do not agree, do not use the Service.
      </P>

      <H>1. What the Service is</H>
      <P>
        PolyStrike Visual is a <strong>user interface</strong> (UI) to public Polymarket
        protocols (CLOB, CTF, Builder Relayer). The Service:
      </P>
      <UL>
        <li>Displays public market data (prices, order book, positions).</li>
        <li>Lets you place limit YES / NO orders via Polymarket CLOB.</li>
        <li>Provides gasless winnings redeem via Polymarket Builder Relayer.</li>
        <li>Stores the minimum necessary data on the server (see Privacy Policy).</li>
      </UL>
      <P>The Service is <strong>not</strong>:</P>
      <UL>
        <li>a broker, exchange, or custodian;</li>
        <li>an investment advisor or financial intermediary;</li>
        <li>a guarantor of order execution or Polymarket availability;</li>
        <li>a counterparty to trades between users.</li>
      </UL>

      <H>2. Non-custodial</H>
      <P>
        Your PM wallet private key <strong>never leaves your browser</strong>. It is encrypted
        with your PIN (AES-256-GCM + PBKDF2-SHA256, 200,000 iterations) and stored in
        localStorage.
      </P>
      <UL>
        <li>The Service <strong>cannot sign a transaction on your behalf</strong> without your PIN.</li>
        <li>The Service <strong>cannot recover</strong> your private key or funds if you forget the PIN or lose the device.</li>
        <li>You bear full responsibility for the security of your PIN, device, and backups of your private key.</li>
      </UL>

      <H>3. Subscription and payment</H>
      <P>The Service is provided on a subscription basis:</P>
      <UL>
        <li><strong>Trial</strong> — 7 days free, full functionality, once per PM wallet.</li>
        <li><strong>Active subscription</strong> — 8.5 USDC for 30 days, paid in USDC on Polygon; the transfer to the treasury address is signed by your key in the browser.</li>
      </UL>
      <P>
        The subscription <strong>does not auto-renew</strong>. When it expires, you do not lose
        access to your funds on Polymarket — you can manage them via polymarket.com. Trading
        inside PolyStrike Visual is blocked until renewal.
      </P>

      <H>4. Refunds</H>
      <P>
        <strong>On-chain USDC payment is irreversible.</strong> A refund is technically impossible
        once the funds have reached the treasury and the subscription has been activated.
      </P>
      <P>Exceptions:</P>
      <UL>
        <li>A technical error on the Service side where the subscription was charged but not activated (contact support).</li>
        <li>A major Polymarket CLOB outage on the payment day fully blocking trading for more than 24 hours.</li>
      </UL>
      <P>In all other cases (changed your mind, not using, unsatisfied) — no refund is issued.</P>

      <H>5. Trading risks</H>
      <P>
        Trading predictions on Polymarket carries the <strong>risk of total loss of funds</strong>.
        You confirm that:
      </P>
      <UL>
        <li>You understand how prediction markets and CLOB orders work.</li>
        <li>You assess the risks of each trade yourself.</li>
        <li>You do not rely on the Service&rsquo;s interface as investment advice.</li>
        <li>You accept that prices on Polymarket are volatile, liquidity is limited, and order execution is not guaranteed.</li>
      </UL>

      <H>6. Jurisdiction and availability</H>
      <P>
        Polymarket CLOB blocks trading requests from a number of jurisdictions (US, UK, Germany,
        France, Italy and others). The Service <strong>does not check</strong> user geolocation — you are
        responsible for ensuring your use of the Service complies with the laws of your jurisdiction.
      </P>
      <P>You must not use the Service:</P>
      <UL>
        <li>from jurisdictions where prediction markets are prohibited;</li>
        <li>for purposes that violate KYC/AML obligations;</li>
        <li>via automated bots (except your own tools that do not harm market integrity).</li>
      </UL>

      <H>7. Limitation of liability</H>
      <P>
        The Service is provided &ldquo;as is&rdquo;, without any express or implied warranties. The owner
        of the Service is not liable for:
      </P>
      <UL>
        <li>Losses from user trading decisions.</li>
        <li>Failures, delays, or inaccuracies of data from Polymarket or the blockchain.</li>
        <li>User compromise (private-key leak, phishing, weak PIN).</li>
        <li>Third-party actions (Polymarket, RPC providers, Telegram).</li>
      </UL>
      <P>
        The maximum aggregate liability of the Service to a user is limited to the amount of
        subscription fees paid by that user during the last 12 months.
      </P>

      <H>8. Changes to the Terms</H>
      <P>
        We may update these Terms. Material changes will be posted in the ReadmeDialog inside the
        App. Continued use after notice = agreement.
      </P>

      <H>9. Termination</H>
      <P>
        We may suspend or terminate the Service for a specific user who violates these Terms. The
        user may stop using the Service at any time — funds on Polymarket remain under the
        user&rsquo;s control.
      </P>

      <H>10. Contact</H>
      <P>
        <strong>Support:</strong> support@polystrike.app<br />
        <strong>Telegram:</strong> @PolyStrike_autobot
      </P>
    </>
  )
})

export const PrivacyContent = memo(function PrivacyContent() {
  return (
    <>
      <Meta>Version 1.0 · Date: 2026-04-23</Meta>

      <P>
        We keep data to a minimum. Your private key never reaches our server — it lives only in
        your browser, encrypted with your PIN.
      </P>

      <H>What we store</H>
      <UL>
        <li>Your wallet address, so we know which account has an active subscription.</li>
        <li>Trial and paid-until dates.</li>
        <li>A record of subscription payments (for audit and support).</li>
        <li>A Telegram ID, only if you signed in via Telegram Mini App.</li>
      </UL>

      <H>What we do not store</H>
      <UL>
        <li>Your private key, PIN, or anything derived from them.</li>
        <li>Your email, phone, name, or location.</li>
        <li>Your Polymarket trades — those live on Polymarket.</li>
      </UL>

      <H>What we do with it</H>
      <P>
        Only what the Service needs to work: checking your subscription status and helping you with
        support requests. We do not sell your data, share it for advertising, or profile you.
      </P>

      <H>Who else sees it</H>
      <P>
        Only the parties needed to deliver the product: Polymarket (public protocol — orders and
        your wallet address are on-chain), Binance (for live price), your Polygon RPC provider,
        and optionally Sentry (errors) and Telegram (if used via Mini App).
      </P>

      <H>Your rights</H>
      <UL>
        <li><strong>Access:</strong> email support to request a copy of the data we hold about you.</li>
        <li><strong>Deletion:</strong> email support to request deletion (may end your subscription).</li>
        <li><strong>Local data:</strong> erase it any time via &ldquo;Reset wallet&rdquo; in the Wallet dialog.</li>
      </UL>

      <H>Cookies and trackers</H>
      <P>
        None. No cookies, no analytics. The only optional client-side tool is Sentry, and only
        when explicitly enabled.
      </P>

      <H>Contact</H>
      <P>
        <strong>Support:</strong> support@polystrike.app<br />
        <strong>Data Protection:</strong> privacy@polystrike.app
      </P>

      <H>Updates</H>
      <P>Material changes are posted in the Info dialog. Continued use = agreement.</P>
    </>
  )
})
